import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Box, ListItemIcon, ListItemText, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { User } from 'models/user';
import { getExternalTechnicians } from 'apollo/templates/user';
import AddIcon from '@mui/icons-material/Add';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import ConnectedExternalUsers from 'components/shared/connected-external-users/connected-external-users.component';

type ExternalUsersDropdownProps = {
    selectedUsers: User[];
    onAddExtUser: (user: User) => void;
    onRemoveExtUser: (user: User) => void;
};
const ExternalUsersDropdown: FC<ExternalUsersDropdownProps> = ({
    onAddExtUser,
    onRemoveExtUser,
    selectedUsers,
}: ExternalUsersDropdownProps) => {
    const { t: translate } = useTranslation('orders');
    const [externalTechnicians, setExternalTechnicians] = useState<User[]>();
    const [notConnectedTechnicians, setNotConnectedTechnicians] = useState<User[]>();
    const [connectedTechnicians, setConnectedTechnicians] = useState<User[]>([] as User[]);
    const [inputValue, setInputValue] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();

    useQuery(getExternalTechnicians, {
        onCompleted: ({ technicians }) => {
            setExternalTechnicians(technicians);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const updateDropdown = (connectedTechnicians: User[]) => {
        const connectedTechObj = connectedTechnicians.reduce(
            (acc, tech: User) => {
                acc[tech._id] = true;
                return acc;
            },
            {} as { [key: string]: boolean },
        );

        const nonConnectedTechs = (externalTechnicians || []).filter((tech) => {
            return !connectedTechObj[tech._id];
        });
        setNotConnectedTechnicians(nonConnectedTechs);
    };

    useEffect(() => {
        const connectedTechnicians = selectedUsers || [];
        updateDropdown(connectedTechnicians);
        setConnectedTechnicians(connectedTechnicians);
    }, [selectedUsers, externalTechnicians]);

    const addExternalTechnician = (technician: User) => {
        const connectedTechs = [...connectedTechnicians, technician];
        setConnectedTechnicians(connectedTechs);
        updateDropdown(connectedTechs);
        onAddExtUser(technician);
    };

    const handleRemoveExternalTechnician = (technician: User) => {
        const connectedTechs = connectedTechnicians.filter((connectedTech) => connectedTech._id !== technician._id);
        setConnectedTechnicians(connectedTechs);
        updateDropdown(connectedTechs);
        setInputValue('');
        onRemoveExtUser(technician);
    };

    return (
        <Box>
            <Box>
                <Autocomplete
                    fullWidth
                    disableClearable
                    options={notConnectedTechnicians || []}
                    getOptionLabel={(technician) => technician.fullName}
                    renderOption={(props, technician) => (
                        <MenuItem {...props} key={technician._id} value={technician._id}>
                            <ListItemIcon>
                                <AddIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{technician.fullName}</ListItemText>
                        </MenuItem>
                    )}
                    renderInput={(params) => <TextField {...params} label={translate('ADD_EXTERNAL_USER')} />}
                    inputValue={inputValue}
                    onChange={(_, technician) => {
                        addExternalTechnician(technician);
                    }}
                    onInputChange={(_, value, reason) => {
                        if (reason === 'reset') {
                            setInputValue('');
                            return;
                        }
                        setInputValue(value);
                    }}
                />
            </Box>
            <Box mt={2}>
                {connectedTechnicians ? (
                    <ConnectedExternalUsers
                        onRemove={handleRemoveExternalTechnician}
                        connectedExternalTechnicians={connectedTechnicians}
                    />
                ) : undefined}
            </Box>
        </Box>
    );
};

export default ExternalUsersDropdown;
